import { lazy, Suspense } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import Hero from "@/components/Hero";
import { usePageAnalytics } from "@/hooks/usePageAnalytics";
import { usePageMeta } from "@/hooks/usePageMeta";
import { ogStatic } from "@/lib/og";
import { trackEvent } from "@/lib/gtag";

const About = lazy(() => import("@/components/About"));
const Projects = lazy(() => import("@/components/Projects"));
const CaseStudies = lazy(() => import("@/components/CaseStudies"));
const LatestPosts = lazy(() => import("@/components/LatestPosts").then((m) => ({ default: m.LatestPosts })));
const FAQ = lazy(() => import("@/components/FAQ"));

const SectionFallback = () => <div className="min-h-[400px]" aria-hidden="true" />;

const Index = () => {
  usePageAnalytics("/", "Homepage");
  usePageMeta({
    title: "Lester Oliver - AI Consulting & Rapid POCs",
    description: "AI consulting and rapid POCs for startups and growth-stage teams. Turn your idea into a working prototype in days, not quarters. Based in Virginia, USA.",
    canonical: "https://lesteroliver.com",
    ogImage: ogStatic("home"),
  });

  const websiteSchema = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    name: "Lester Oliver",
    url: "https://lesteroliver.com",
    description: "Solutions consultant and rapid POC builder turning ideas into working prototypes.",
    author: {
      "@type": "Person",
      name: "Lester Oliver",
    },
  };

  const personSchema = {
    "@context": "https://schema.org",
    "@type": "Person",
    name: "Lester Oliver",
    url: "https://lesteroliver.com",
    jobTitle: "Solutions Consultant & POC Builder",
    description: "Solutions consultant and rapid POC builder specializing in turning ideas into working prototypes using AI-powered development. Based in Virginia, USA.",
    address: {
      "@type": "PostalAddress",
      addressRegion: "Virginia",
      addressCountry: "US",
    },
    nationality: { "@type": "Country", name: "India" },
    knowsAbout: [
      "Solutions Consulting",
      "Rapid Prototyping",
      "Artificial Intelligence",
      "Machine Learning",
      "Large Language Models",
      "RAG Systems",
      "Project Management",
      "React",
      "TypeScript",
      "Python",
    ],
    hasOccupation: {
      "@type": "Occupation",
      name: "Solutions Consultant & POC Builder",
      skills: "Rapid POC development, solutions consulting, AI-powered prototyping, project management",
    },
    sameAs: [
      "https://linkedin.com/in/lesteroliver",
      "https://github.com/lesteroliver911",
    ],
  };

  const professionalServiceSchema = {
    "@context": "https://schema.org",
    "@type": "ProfessionalService",
    "@id": "https://lesteroliver.com/#service",
    name: "Lester Oliver - Solutions Consulting and Rapid POC Development",
    description:
      "Independent solutions consulting and rapid POC development. Turn ideas into working prototypes in days, not months, using AI-assisted engineering.",
    url: "https://lesteroliver.com",
    image: "https://lesteroliver.com/og-image.png",
    priceRange: "$$",
    areaServed: [
      { "@type": "Country", name: "United States" },
      { "@type": "Place", name: "Remote / Worldwide" },
    ],
    provider: {
      "@type": "Person",
      name: "Lester Oliver",
      url: "https://lesteroliver.com/about",
    },
    address: {
      "@type": "PostalAddress",
      addressRegion: "Virginia",
      addressCountry: "US",
    },
    serviceType: [
      "Rapid POC Development",
      "AI Prototyping",
      "Solutions Consulting",
      "Solution Design",
      "Project Management",
    ],
    hasOfferCatalog: {
      "@type": "OfferCatalog",
      name: "Consulting Services",
      itemListElement: [
        {
          "@type": "Offer",
          itemOffered: {
            "@type": "Service",
            name: "Rapid POC Development",
            description:
              "Functional prototypes built with AI-assisted coding, delivered in days at a fraction of traditional dev cost.",
          },
        },
        {
          "@type": "Offer",
          itemOffered: {
            "@type": "Service",
            name: "AI Strategy & Consulting",
            description:
              "Structured sessions to scope, validate, and de-risk AI product ideas before committing engineering budget.",
          },
        },
        {
          "@type": "Offer",
          itemOffered: {
            "@type": "Service",
            name: "Solution Design",
            description:
              "Translate fuzzy business problems into clear technical requirements, workflows, and actionable plans.",
          },
        },
      ],
    },
    sameAs: [
      "https://linkedin.com/in/lesteroliver",
      "https://github.com/lesteroliver911",
    ],
  };

  return (
    <>
      <main>
        <Hero />
        <Suspense fallback={<SectionFallback />}>
          <About />
          <Projects />
          <CaseStudies />
          <LatestPosts />
          <FAQ />
        </Suspense>
        <section className="py-12 px-6">
          <div className="container mx-auto max-w-6xl">
            <div className="rounded-2xl border border-border bg-card p-10 md:p-14 text-center">
              <h2 className="text-2xl md:text-3xl font-medium text-foreground mb-4">
                Let's Build Something
              </h2>
              <p className="text-muted-foreground leading-relaxed max-w-2xl mx-auto mb-8">
                Have an idea you want to validate fast? Need a prototype, a consulting session, or someone who bridges the gap between business and tech? Let's talk.
              </p>
              <Link
                to="/contact"
                onClick={() => trackEvent("cta_click", { location: "homepage_bottom" })}
                className="inline-flex items-center gap-2 bg-foreground/80 text-background px-8 py-3 rounded-full font-medium hover:bg-foreground/70 transition-colors"
              >
                Get in touch
                <ArrowRight size={18} />
              </Link>
            </div>
          </div>
        </section>
      </main>

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(websiteSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(personSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(professionalServiceSchema) }}
      />
    </>
  );
};

export default Index;
